'use strict';

/*
 * videoSeek.js -- key moment and transcript timestamp links seek the watch page's player in place
 * (specs/005-video-library, research R3).
 *
 * Every link is a real `<a href="?t={seconds}" data-seek="{seconds}">`: without JavaScript it reloads the page and
 * the server starts the iframe at that second. With JavaScript the click is intercepted here instead.
 *
 * YouTube: the server-rendered iframe already has enablejsapi=1. If a YT.Player is already attached to it we reuse
 * that one (YT.get(iframe.id)) - Umbraco Engage's video tracking loads the IFrame API on page load and wraps every
 * YouTube iframe, and a second `new YT.Player` on the same iframe never fires onReady. Otherwise, on the FIRST seek,
 * we load the API ourselves and wrap the iframe. Then seekTo + playVideo. Seeks made before the player is ready are
 * queued; the last one wins.
 * The race the other way round is safe too: Engage makes the same YT.get(iframe.id) check before creating its own
 * player, and `new YT.Player` gives the iframe an id, so if we get there first Engage attaches its tracking to our
 * player. Whichever script is first, there is one player. (Checked 2026-10-15 on dev with Engage's script blocked:
 * our player got id "widget2" and Engage's check, run verbatim, found it.)
 * Vimeo: the documented postMessage methods setCurrentTime and play; no library is bundled.
 *
 * Analytics: GA4 `video_seek` (video_id, video_title, seek_seconds, seek_source) and Engage "Video" / "Seek" /
 * video_id, per contracts/analytics-events.md. Both are guarded; gtag only exists on www.proworks.com.
 *
 * If anything throws, or the YouTube API hasn't loaded within 5 s, the link is followed normally, so a visitor
 * always gets to the moment.
 */

var YT_API_SRC = 'https://www.youtube.com/iframe_api';
// If the API hasn't loaded by then (blocked, offline), follow the link: the server starts the player there.
var YT_API_TIMEOUT_MS = 5000;

var ytPlayer = null;
var ytReady = false;
var ytLoading = false;
var pendingSeek = null;
var pendingHref = null;

function getPlayerElements() {
    var wrapper = document.querySelector('.video-page .video-player');
    var iframe = wrapper ? wrapper.querySelector('iframe') : null;
    return wrapper && iframe ? { wrapper: wrapper, iframe: iframe } : null;
}

function isYouTube(iframe) {
    return /^https:\/\/www\.youtube-nocookie\.com\/embed\//.test(iframe.src);
}

function isVimeo(iframe) {
    return /^https:\/\/player\.vimeo\.com\/video\//.test(iframe.src);
}

function ytSeek(seconds) {
    ytPlayer.seekTo(seconds, true);
    ytPlayer.playVideo();
}

function onYouTubeReady() {
    ytReady = true;
    if (pendingSeek !== null) {
        var seconds = pendingSeek;
        pendingSeek = null;
        pendingHref = null;
        ytSeek(seconds);
    }
}

// A player another script (Umbraco Engage) already attached to this iframe, if any.
function getExistingPlayer(iframe) {
    var yt = window.YT;
    return iframe.id && yt && typeof yt.get === 'function' ? yt.get(iframe.id) || null : null;
}

// YT.Player only gains its methods once the player is ready.
function isPlayerReady(player) {
    return typeof player.seekTo === 'function' && typeof player.playVideo === 'function';
}

function attachYouTubePlayer(iframe) {
    var existing = getExistingPlayer(iframe);
    if (existing) {
        ytPlayer = existing;
        if (isPlayerReady(existing)) {
            onYouTubeReady();
        } else {
            existing.addEventListener('onReady', onYouTubeReady);
        }
        return;
    }
    ytPlayer = new window.YT.Player(iframe, { events: { onReady: onYouTubeReady } });
}

function seekYouTube(iframe, seconds, href) {
    if (ytReady) {
        ytSeek(seconds);
        return;
    }

    pendingSeek = seconds;
    pendingHref = href;
    if (ytLoading) {
        return;
    }
    ytLoading = true;

    window.setTimeout(function () {
        if (!ytReady && pendingHref) {
            window.location.href = pendingHref;
        }
    }, YT_API_TIMEOUT_MS);

    if (window.YT && typeof window.YT.Player === 'function') {
        attachYouTubePlayer(iframe);
        return;
    }

    // Chain rather than replace, in case something else on the page also waits for the API.
    var previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
        if (typeof previous === 'function') {
            previous();
        }
        attachYouTubePlayer(iframe);
    };

    var script = document.createElement('script');
    script.src = YT_API_SRC;
    script.async = true;
    document.head.appendChild(script);
}

function seekVimeo(iframe, seconds) {
    var target = 'https://player.vimeo.com';
    iframe.contentWindow.postMessage(JSON.stringify({ method: 'setCurrentTime', value: seconds }), target);
    iframe.contentWindow.postMessage(JSON.stringify({ method: 'play' }), target);
}

function scrollToPlayer(wrapper) {
    var rect = wrapper.getBoundingClientRect();
    if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
        return;
    }
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    wrapper.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
}

function track(wrapper, seconds, source) {
    var videoId = wrapper.getAttribute('data-video-id') || '';

    if (typeof window.gtag === 'function') {
        window.gtag('event', 'video_seek', {
            video_id: videoId,
            video_title: wrapper.getAttribute('data-video-title') || '',
            seek_seconds: seconds,
            seek_source: source
        });
    }

    if (typeof window.umbEngage === 'function') {
        window.umbEngage('send', 'event', 'Video', 'Seek', videoId);
    }
}

function onClick(e) {
    var link = e.target.closest ? e.target.closest('.video-page a[data-seek]') : null;
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
    }

    var seconds = parseInt(link.getAttribute('data-seek'), 10);
    var player = getPlayerElements();
    if (isNaN(seconds) || seconds < 0 || !player) {
        return; // let the browser follow the ?t= link
    }

    e.preventDefault();

    try {
        if (isYouTube(player.iframe)) {
            seekYouTube(player.iframe, seconds, link.href);
        } else if (isVimeo(player.iframe)) {
            seekVimeo(player.iframe, seconds);
        } else {
            window.location.href = link.href;
            return;
        }

        scrollToPlayer(player.wrapper);
        track(player.wrapper, seconds, link.closest('.video-key-moments') ? 'key_moment' : 'transcript');
    } catch (err) {
        window.location.href = link.href;
    }
}

function init() {
    document.addEventListener('click', onClick);
}

module.exports = { init: init };
